import jsPDF from "jspdf";
import api from "../lib/api.ts";
import dayjs from "dayjs";
import { applyStandardPdfStyles } from "../utils/pdfUtils";
import pdfBackground from "../assets/pdf-background.png";
import placeholder from "../assets/Car-place-holder-PNG.png";
/**
 * Recursively extracts text from a React element or string
 */
const extractText = (node) => {
    if (node === null || node === undefined) return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");

    if (node.props) {
        if (node.props.children) return extractText(node.props.children);
        if (node.type === "span" || node.type === "div") return "";
    }
    return typeof node === "object" ? "" : String(node);
};

/**
 * Loads an image directly and converts to Base64
 */
const getImageAsBase64 = (url) => {
    return new Promise((resolve) => {
        if (!url) return resolve(null);
        const img = new Image();
        img.crossOrigin = "anonymous"; // For canvas export

        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);

                // Convert to JPEG with 0.7 quality
                const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
                resolve(dataUrl);
            } catch (err) {
                console.error("[PDF Gen] Canvas error:", err);
                resolve(null);
            }
        };

        img.onerror = () => {
            console.error(`[PDF Gen] Failed to load image: ${url}`);
            resolve(null);
        };

        img.src = url; // DIRECTLY use the URL, no proxy
    });
};

const loadImage = async (url) => {
    if (!url) return placeholder;
    try {
        const response = await api.get("/products/image-proxy", {
            params: { url },
            responseType: "blob",
        });
        return URL.createObjectURL(response.data);
    } catch {
        return placeholder;
    }
};

// Convert blob URL → base64 (REQUIRED for jsPDF)
const blobUrlToBase64 = (blobUrl) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";

        img.onload = function () {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            const base64 = canvas.toDataURL("image/jpeg");
            resolve(base64);
        };

        img.onerror = reject;
        img.src = blobUrl;
    });
};

/**
 * Generates an A4 PDF for vehicle details — fully dynamic
 */
export const generateVehiclePDF = async (
    carDetail,
    carInfoItems,
    auctionInfoItems,
    imageUrls
) => {
    const doc = new jsPDF("p", "mm", "a4");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    // ---- Determine auction status dynamically ----
    const auctionName =
        auctionInfoItems?.find(([l]) => l === "Auction Name")?.[1] || "";
    const isSold =
        !!carDetail?.soldPrice || carDetail?.ProductStatus === "Sold";
    const isBuyNow = !!carDetail?.buyNowPrice;
    const auctionStatus = isSold
        ? "SOLD"
        : isBuyNow
            ? "Buy Now"
            : auctionName
                ? "Scheduled"
                : "Not Scheduled";

    const carTitle = `${carDetail?.year || ""} ${carDetail?.Make || ""} ${carDetail?.MakeModel || ""
        } ${carDetail?.variant || ""}`.trim().toUpperCase();

    /* ===== PAGE BREAK HELPER ===== */
    const footerHeight = 25; // Define how much space your footer takes at the bottom

    const needsPageBreak = (requiredHeight) => {
        // Check if current Y + required height exceeds (Page Height - Footer Space)
        if (y + requiredHeight > pageHeight - footerHeight) {
            doc.addPage();
            y = margin + 10; // Start a bit lower on the new page
            return true;
        }
        return false;
    };

    /* ===== SECTION HEADER HELPER ===== */
    const drawSection = (title) => {
        const paddingTop = 2;
        const paddingBottom = 2;
        const rectHeight = 7 + paddingTop + paddingBottom;
        const spaceAfter = 5;
        const minimumContentSpace = 15; // Ensure at least 15mm of content fits after the header

        // Check if header + some content will fit. If not, move to next page.
        needsPageBreak(rectHeight + spaceAfter + minimumContentSpace);

        // Draw section background
        doc.setFillColor(230, 230, 230);
        doc.rect(margin, y, contentWidth, rectHeight, "F");

        // Draw section title
        doc.setFontSize(12);
        doc.setTextColor(19, 94, 160);
        doc.setFont("helvetica", "bold");
        doc.text(title, margin + 2, y + paddingTop + 5);

        // Move y position below rectangle
        y += rectHeight + spaceAfter;
    };

    /* ================= HEADER ================= */

    const headerHeight = 32; // slightly taller to match reference

    // Standardized styles will be applied at the end before saving

    // ---- BOTTOM BORDER LINE ----
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.5);
    doc.line(0, headerHeight, pageWidth, headerHeight);

    /* ===== LEFT SIDE — LOGO + TAGLINE ===== */

    const logoW = 48;
    const logoH = 30;
    const logoX = margin;
    const logoY = (headerHeight - logoH) / 2; // vertically centered

    let logoLoaded = false;
    try {
        const logoBase64 = await getImageAsBase64("/skypdflogo.png", false);
        if (logoBase64) {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoW, logoH);
            logoLoaded = true;
        }
    } catch (_) { }

    if (!logoLoaded) {
        // Fallback: draw a simple "S" circle placeholder
        doc.setFillColor(19, 94, 160);
        doc.circle(logoX + 8, headerHeight / 2, 8, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("S", logoX + 8, headerHeight / 2 + 4, { align: "center" });

        // "SKY AUCTION" text next to placeholder
        doc.setTextColor(19, 94, 160);
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text("SKY AUCTION", logoX + 19, headerHeight / 2 - 1);

        // Tagline
        doc.setFontSize(6.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text("Auto Auctions & Online Bidding", logoX + 19, headerHeight / 2 + 5);
    }

    /* ===== RIGHT SIDE — VEHICLE TITLE + LOT + AUCTION STATUS ===== */

    // Starting X for the right-side text block (after the logo area)
    const rightTextStartX = logoLoaded ? logoX + logoW + 8 : logoX + 65;
    const rightTextEndX = pageWidth - margin - 4; // leave a little right padding

    // --- LARGE VEHICLE TITLE ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(19, 94, 160); // near-black, matching reference
    const titleLines = doc.splitTextToSize(carTitle || "Vehicle Details", rightTextEndX - rightTextStartX);
    doc.text(titleLines, rightTextStartX, 16);

    const titleBlockHeight = titleLines.length * 6.5;

    // --- LOT NUMBER line ---
    const lotY = 13 + titleBlockHeight + 2;

    // "LOT NUMBER: " label — bold black
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    const lotLabel = "LOT NUMBER: ";
    doc.text(lotLabel, rightTextStartX, lotY);
    const lotLabelW = doc.getTextWidth(lotLabel);

    // Lot number value — orange/primary color
    doc.setTextColor(15, 74, 125); // orange matching the reference
    doc.setFont("helvetica", "bold");
    const lotValue = carDetail?.lotNumber || "-";
    doc.text(lotValue, rightTextStartX + lotLabelW, lotY);
    const lotValueW = doc.getTextWidth(lotValue);

    // Separator " - "
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    const separator = "  -  ";
    doc.text(separator, rightTextStartX + lotLabelW + lotValueW, lotY);
    const sepW = doc.getTextWidth(separator);

    // "Auction: " label — bold black
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    const auctionLabel = "Auction: ";
    doc.text(auctionLabel, rightTextStartX + lotLabelW + lotValueW + sepW, lotY);
    const auctionLabelW = doc.getTextWidth(auctionLabel);

    // Full status text — orange/blue (matching reference: "Not Schedule / SOLD / Buy Now")
    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 74, 125); // orange for the status value
    const fullStatusText = isSold
        ? "SOLD"
        : isBuyNow
            ? "Buy Now"
            : auctionName
                ? auctionName
                : "Not Schedule / SOLD / Buy Now";
    doc.text(fullStatusText, rightTextStartX + lotLabelW + lotValueW + sepW + auctionLabelW, lotY);

    /* CONTENT START */
    y = headerHeight + 8;

    /* ===== DETAILED SPECIFICATIONS ===== */
    drawSection("DETAILED SPECIFICATIONS");

    y += 4; // 🔥 GAP BETWEEN HEADING & ITEMS

    const rowH = 7;
    doc.setFontSize(9);
    const colWidth = contentWidth / 2;
    const itemH = 8;
    const rowGap = 3; // 👈 controls space between rows

    carInfoItems.forEach(([label, value], i) => {
        const isLeft = i % 2 === 0;
        const x = margin + (isLeft ? 0 : colWidth);
        const displayVal = extractText(value) || "-";

        if (isLeft) needsPageBreak(itemH);

        // Zebra background
        if (Math.floor(i / 2) % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(x, y - 5, colWidth - 2, itemH, "F");
        }

        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        doc.text(`${label}:`, x + 2, y);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        doc.text(displayVal, x + 35, y);

        if (!isLeft || i === carInfoItems.length - 1) {
            y += itemH + rowGap;
        }
    });
    y += 8;

    /* ===== VEHICLE DESCRIPTION ===== */
    drawSection("VEHICLE DESCRIPTION");

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
    const descText =
        carDetail?.description || "No details available for this vehicle.";
    const descLines = doc.splitTextToSize(descText, contentWidth);
    const descHeight = descLines.length * 4;
    needsPageBreak(descHeight + 4);
    doc.text(descLines, margin, y);
    y += descHeight + 5;

    /* ===== AUCTION DETAILS — fully dynamic ===== */

    /* ===== AUCTION DETAILS ===== */
    drawSection("AUCTION DETAILS");

    y += 4; // gap after heading

    const auctionColWidth = contentWidth / 2;
    const auctionItemH = 7;
    const auctionRowGap = 3;
    doc.setFontSize(8); // smaller font for auction details

    auctionInfoItems.forEach(([label, rawValue], i) => {
        const isLeft = i % 2 === 0;
        const x = margin + (isLeft ? 0 : auctionColWidth);
        const displayVal = extractText(rawValue) || "-";

        if (isLeft) needsPageBreak(auctionItemH + auctionRowGap);

        // Zebra background
        if (Math.floor(i / 2) % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(x, y - 5, auctionColWidth - 2, auctionItemH + auctionRowGap - 1, "F");
        }

        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        doc.text(`${label}:`, x + 2, y);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        doc.text(displayVal, x + 35, y);

        // Increment Y after right column or last item
        if (!isLeft || i === auctionInfoItems.length - 1) {
            y += auctionItemH + auctionRowGap;
        }
    });

    y += 6; // extra space after section

    /* ===== IMAGES GRID ===== */
    if (imageUrls && imageUrls.length > 0) {
        drawSection("VEHICLE IMAGES");

        const imgWidth = (contentWidth - 10) / 3;
        const imgHeight = imgWidth * 0.65;
        const gap = 5;

        // Filter out placeholder/empty URLs first
        const validUrls = imageUrls.filter(
            (url) =>
                url &&
                !url.includes("place-holder") &&
                !url.includes("placeholder")
        );
        console.log("validUrls", validUrls);
        let col = 0;
        let rowStartY = y; // absolute Y of current row
        let imagesPlaced = 0;

        for (const url of validUrls) {
            if (col === 0 && imagesPlaced > 0) {
                rowStartY += imgHeight + gap;

                if (rowStartY + imgHeight > pageHeight - margin) {
                    doc.addPage();
                    rowStartY = margin + 5;
                }
            }

            const x = margin + col * (imgWidth + gap);

            try {
                // 🔥 LOAD VIA PROXY
                const blobUrl = await loadImage(url);

                // 🔥 CONVERT TO BASE64
                const imgBase64 = await blobUrlToBase64(blobUrl);

                if (imgBase64) {
                    const fmt = imgBase64.startsWith("data:image/png")
                        ? "PNG"
                        : "JPEG";

                    doc.addImage(imgBase64, fmt, x, rowStartY, imgWidth, imgHeight);

                    imagesPlaced++;
                    col = (col + 1) % 3;
                }
            } catch (err) {
                console.log("Image load failed", err);
            }
        }
    }

    /* ===== APPLY STANDARD STYLES (ARCS & FOOTER) ===== */
    const generatedDate = dayjs().format("MMMM D, YYYY [at] hh:mm A");
    applyStandardPdfStyles(doc, generatedDate);

    /* ===== SAVE ===== */
    doc.save(`SKY-AUCTION-${carDetail?.lotNumber || "vehicle"}.pdf`);
};